import React, { FC, ReactNode } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material';

const TOP_PX_OFFSET = 80; // Height of navbar to offset sticky positioning

interface ISearchMapToggleProps {
  /** Whether the map is active (visible on mobile) */
  isMapActive: boolean;
  /** Map content to render */
  children: ReactNode;
}

/**
 * Wrapper component that handles map visibility toggling on mobile.
 * Provides sticky positioning on desktop and toggle-based visibility on mobile.
 *
 * This component encapsulates the presentation logic (how to display the map)
 * while allowing the map content itself to be passed as children.
 */
const SearchMapToggle: FC<ISearchMapToggleProps> = ({ isMapActive, children }) => {
  const lgAndDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  // On desktop, map is always active
  const effectiveIsMapActive = lgAndDown ? isMapActive : true;

  return (
    <Grid
      item
      aria-hidden={!effectiveIsMapActive}
      sx={(theme) => ({
        flex: 1,
        position: 'sticky',
        top: `${TOP_PX_OFFSET}px`, // Account for navbar
        right: 0,
        width: 'calc(100% - 48em)', // Full width minus left container width
        minWidth: '24rem', // Prevent overly narrow map on smaller desktop widths
        height: `calc(100vh - ${TOP_PX_OFFSET}px)`, // Subtract navbar from height
        zIndex: 1, // Low z-index so global overlays/footers with higher z-index values appear above the map
        [theme.breakpoints.down('lg')]: effectiveIsMapActive ? {
          // Map is toggled on - visible on mobile, filling the space under the search header
          top: 0,
          position: 'relative',
          width: '100%',
          flex: '1 1 0',
          height: 'auto',
          minHeight: 0,
          visibility: 'visible',
          pointerEvents: 'auto',
          zIndex: 'auto'
        } : {
          // Override hidden state when map is toggled off
          top: 'auto',
          position: 'absolute',
          width: '100%',
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1
        },
      })}
    >
      {children}
    </Grid>
  );
};

export default SearchMapToggle;

